<h1 class="bold mt-giant-r">How to forms</h1>
<h2>with Bootstrap and Swiss Post specific styles</h2>

<nav class="mt-giant-r light font-curve-regular">
  <ul>
    <li><a href="#page-layout">Page Layout</a></li>
    <li><a href="#basic-input">Basic input</a></li>
    <li><a href="#vertical-spacing">Vertical spacing</a></li>
    <li><a href="#rows-n-cols">Rows'n'cols</a></li>
    <li><a href="#fields-with-custom-width">Fields with custom width</a></li>
    <li><a href="#form-buttons">Form buttons</a></li>
    <li><a href="#floating-labels">Floating labels</a></li>
    <li><a href="#validation-messages">Validation messages</a></li>
    <li><a href="#hints">Hints</a></li>
  </ul>
</nav>

<section>
  <h2 id="page-layout" class="bold mt-giant-r">Page layout</h2>
  <p class="light font-curve-regular">
    All things need to be contained inside a container. The container limits the size of the content
    and centers it on the page.
  </p>
  <code class="block" [highlight]="container" [languages]="['html']"></code>

  <h2 id="basic-input" class="bold mt-giant-r">Basic input</h2>
  <p class="light font-curve-regular">
    For accessibility reasons, form fields should always have a label linked via the id/for
    attributes. Screen-readers will announce the label when the input gets the focus. If there is no
    label, screen-reader users have no idea what the input should be, even if there is other text
    next to the field.
  </p>
  <div class="bg-light p-big">
    <label class="form-label" for="basic-input-firstname">Firstname</label>
    <input type="text" id="basic-input-firstname" class="form-control" placeholder="Firstname" />
  </div>
  <code class="block" [highlight]="basicInput" [languages]="['html']"></code>

  <h2 id="vertical-spacing" class="bold mt-giant-r">Vertical spacing</h2>
  <p class="light font-curve-regular">
    To add space between input, you can use
    <a routerLink="/post-samples/sizing#padding-and-margin">margin utilities</a>
    .
  </p>
  <div class="bg-light p-big">
    <div class="mb-regular">
      <label class="form-label" for="vertical-spacing-firstname">Firstname</label>
      <input
        type="text"
        id="vertical-spacing-firstname"
        class="form-control"
        placeholder="Firstname"
      />
    </div>
    <div class="mb-regular">
      <label class="form-label" for="vertical-spacing-lastname">Lastname</label>
      <input
        type="text"
        id="vertical-spacing-lastname"
        class="form-control"
        placeholder="Lastname"
      />
    </div>
  </div>
  <code class="block" [highlight]="verticalSpacing" [languages]="['html']"></code>

  <h2 id="rows-n-cols" class="bold mt-giant-r" style="font-variant-ligatures: none">Rows'n'cols</h2>
  <p class="light font-curve-regular">
    If you want a form field to span the whole available width, you don't need a row nor a col. You
    only need those, if you want to place two things next to each other.
  </p>
  <p class="light font-curve-regular">
    Simply use
    <code>.row</code>
    and
    <code>.col</code>
    to have the width distributed evenly between all inputs. To make it responsive, you can add
    <code>.row-cols-*</code>
    classes to the row (
    <a href="https://getbootstrap.com/docs/5.1/layout/grid/#row-columns">Bootstrap Row Columns</a>
    ).
  </p>
  <div class="bg-light p-big">
    <div class="row row-cols-1 row-cols-md-2">
      <div class="col">
        <label class="form-label" for="rows-n-cols-firstname">Firstname</label>
        <input
          type="text"
          id="rows-n-cols-firstname"
          class="form-control"
          placeholder="Firstname"
        />
      </div>
      <div class="col">
        <label class="form-label" for="rows-n-cols-lastname">Lastname</label>
        <input type="text" id="rows-n-cols-lastname" class="form-control" placeholder="Lastname" />
      </div>
    </div>
  </div>
  <code class="block" [highlight]="rowSimple" [languages]="['html']"></code>
  <br />
  <p class="light font-curve-regular">
    For finer adjustment, you can also use the
    <code>.col-*</code>
    classes (
    <a href="https://getbootstrap.com/docs/5.1/layout/grid/#setting-one-column-width">
      Bootstrap Columns
    </a>
    ) on your columns. For example for a 2/1 ratio, given that a row has twelve columns in total,
    just add
    <code>.col-8</code>
    and
    <code>.col-4</code>
    to you two columns respectively.
  </p>
  <div class="bg-light p-big">
    <div class="row">
      <div class="col-8">
        <label class="form-label" for="rows-n-cols-city">City</label>
        <input type="text" id="rows-n-cols-city" class="form-control" placeholder="City" />
      </div>
      <div class="col">
        <label class="form-label" for="rows-n-cols-state">State</label>
        <input type="text" id="rows-n-cols-state" class="form-control" placeholder="State" />
      </div>
    </div>
  </div>
  <code class="block" [highlight]="colSimple" [languages]="['html']"></code>

  <h2 id="fields-with-custom-width" class="bold mt-giant-r">Fields with custom width</h2>
  <p class="light font-curve-regular">
    Some fields may not fit into the twelve row schema, they need a custom width. The PLZ field may
    be one of those cases. Two
    <code>.col</code>
    next to each other will have equal width. If one of the cols has the
    <code>.col-auto</code>
    class, this col is sized based on it's content. The other row is stretched to the container
    width. With this setup, we can define a max-width on the input field to shrink it.
  </p>
  <div class="bg-light p-big">
    <div class="row">
      <div class="col-auto">
        <label class="form-label" for="custom-width-zip">Zip</label>
        <input
          type="text"
          id="custom-width-zip"
          class="form-control w-giant"
          maxlength="4"
          placeholder="Zip"
        />
      </div>

      <div class="col">
        <label class="form-label" for="custom-width-city">City</label>
        <input type="text" id="custom-width-city" class="form-control" placeholder="City" />
      </div>
    </div>
  </div>
  <code class="block" [highlight]="customWidth" [languages]="['html']"></code>

  <h2 id="form-buttons" class="bold mt-giant-r">Form buttons</h2>
  <p class="light font-curve-regular">
    Buttons, often at the end of the form are right aligned. We can achieve this with the classes
    <code>d-flex flex-row-reverse</code>
    . This will ensure that the primary button is focused first (because of the markup order) but is
    still aligned to the right. To get some margin between the button we can use the
    <code>.gap-mini</code>
    class. Note that for the buttons, we don't need any row/col structure. Read more about gap
    classes at
    <a routerLink="https://getbootstrap.com/docs/5.1/utilities/spacing/#gap">Bootstrap Gaps</a>
    .
  </p>
  <div class="bg-light p-big">
    <div class="mb-regular">
      <label class="form-label" for="form-buttons-firstname">Firstname</label>
      <input type="text" id="form-buttons-firstname" class="form-control" placeholder="Firstname" />
    </div>

    <div class="d-flex flex-row-reverse gap-mini">
      <button class="btn btn-primary">Send</button>
      <button class="btn btn-secondary">Cancel</button>
    </div>
  </div>
  <code class="block" [highlight]="buttons" [languages]="['html']"></code>

  <h2 id="floating-labels" class="bold mt-giant-r">Floating labels</h2>
  <p class="light font-curve-regular">
    For internet applications, bigger input fields with floating labels are the Post way of creating
    forms. The particularity of these fields, is that the
    <code>&lt;label&gt;</code>
    and
    <code>&lt;input&gt;</code>
    elements are inverted and wrapped in a
    <code>.form-floating</code>
    div. In addition, the input field needs a placeholder attribute, preferably equal to a single
    space.
  </p>
  <div class="bg-light p-big">
    <div class="row">
      <div class="col">
        <div class="form-floating">
          <input class="form-control" id="floating-labels-firstname" placeholder=" " type="email" />
          <label class="form-label" for="floating-labels-firstname">Firstname</label>
        </div>
      </div>
      <div class="col">
        <div class="form-floating">
          <input class="form-control" id="floating-labels-lastname" placeholder=" " type="email" />
          <label class="form-label" for="floating-labels-lastname">Lastname</label>
        </div>
      </div>
    </div>
  </div>
  <code class="block" [highlight]="floatingLabels" [languages]="['html']"></code>

  <h2 id="validation-messages" class="bold mt-giant-r">Validation messages</h2>
  <p class="light font-curve-regular">
    Validation messages are placed directly after the
    <code>&lt;input&gt;</code>
    (or after the
    <code>&lt;label&gt;</code>
    in the case of floating label). Showing and hiding of these messages is typically controlled by
    adding and removing validation classes on the input:
  </p>
  <ul class="light font-curve-regular px-giant">
    <li>
      The message needs the class
      <code>.invalid-feedback/.valid-feedback</code>
      ,
    </li>
    <li>
      <ul class="list-unstyled">
        <li>
          The form needs the class
          <code>.was-validated</code>
          . With this class you can manage when to display errors. Usually, errors should not be
          shown on untouched fields.
        </li>
        <li><ins>Or</ins></li>
        <li>
          The input field needs the class
          <code>.is-invalid/.is-valid</code>
          ,
        </li>
      </ul>
    </li>
  </ul>
  <div class="bg-light p-big">
    <form action="/" class="was-validated">
      <!-- Form Control -->
      <div class="row">
        <div class="col">
          <div class="form-floating">
            <input
              id="FormControlInvalid"
              type="text"
              class="form-control is-invalid"
              placeholder=" "
              required
            />
            <label class="form-label" for="FormControlInvalid">Invalid Input</label>
            <p class="invalid-feedback">Error message</p>
          </div>
        </div>
        <div class="col">
          <div class="form-floating">
            <input
              id="FormControlValid"
              type="text"
              class="form-control is-valid"
              placeholder=" "
              value="Value"
            />
            <label class="form-label" for="FormControlValid">Valid Input</label>
            <p class="valid-feedback">Success message (optional)</p>
          </div>
        </div>
      </div>
    </form>
  </div>
  <code class="block" [highlight]="validation" [languages]="['html']"></code>

  <h2 id="hints" class="bold mt-giant-r">Hints</h2>
  <p class="light font-curve-regular">
    Hints can help users better understand the type of information they need to provide. Hints, like
    validation messages, must be placed after the
    <code>&lt;input&gt;</code>
    (or
    <code>&lt;label&gt;</code>
    in the case of floating label) and after validation messages if there are some.
  </p>
  <p class="light font-curve-regular">
    Hints use the class
    <code>.form-text</code>
    .
  </p>
  <p class="light font-curve-regular">
    To enable screen-readers to detect and read your hints, link the
    <code>&lt;input&gt;</code>
    with the aria-labelledby attribute to the hint via id.
  </p>
  <div class="bg-light p-big">
    <div class="form-floating">
      <input
        id="hints-firstname"
        aria-labelledby="firstname-hint"
        type="text"
        class="form-control"
        placeholder=" "
      />
      <label class="form-label" for="hints-firstname">Firstname</label>
      <div id="firstname-hint" class="form-text d-flex align-items-center gap-mini">
        <i class="pi pi-info" aria-hidden="true"></i>
        <span>Also provide any middle names in this field</span>
      </div>
    </div>
  </div>
  <code class="block" [highlight]="hints" [languages]="['html']"></code>
</section>
